import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BookOpen, ArrowLeft, Users, IndianRupee, GraduationCap, UserCheck } from 'lucide-react';

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [feeStructure, setFeeStructure] = useState([]);
  const [timetableData, setTimetableData] = useState({ entries: [], grouped: {} });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!id) return;
    try {
      const feeParams = { class_id: id };
      if (user?.school_id != null) feeParams.school_id = user.school_id;
      const academicYearId = user?.school_id
        ? localStorage.getItem(`selected_academic_year_id_${user.school_id}`)
        : null;
      const studentParams = {
        class_id: id,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const timetableParams = {
        class_id: id,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      const [classRes, studentsRes, feeRes, timetableRes] = await Promise.allSettled([
        api.get(`/classes/${id}`),
        api.get('/students', { params: studentParams }),
        api.get('/fees/structure', { params: feeParams }),
        api.get('/timetable', { params: timetableParams }),
      ]);
      setClassInfo(classRes.status === 'fulfilled' ? (classRes.value?.data?.data ?? null) : null);
      setStudents(studentsRes.status === 'fulfilled' ? (studentsRes.value?.data?.data ?? []) : []);
      setFeeStructure(feeRes.status === 'fulfilled' ? (feeRes.value?.data?.data ?? []) : []);
      setTimetableData(timetableRes.status === 'fulfilled' ? (timetableRes.value?.data?.data ?? { entries: [], grouped: {} }) : { entries: [], grouped: {} });
      if (classRes.status === 'rejected') console.error('Class detail failed', classRes.reason);
      if (studentsRes.status === 'rejected') console.error('Students failed', studentsRes.reason);
      if (feeRes.status === 'rejected') console.error('Fee structure failed', feeRes.reason);
      if (timetableRes.status === 'rejected') console.error('Timetable failed', timetableRes.reason);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, user?.school_id]);

  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [id, user?.school_id]);

  const subjectsInClass = useMemo(() => {
    if (classInfo?.subjects?.length) {
      return classInfo.subjects.map((s) => ({
        id: s.id,
        name: s.name,
        teacher: null,
      }));
    }
    const entries = timetableData.entries || [];
    const byId = new Map();
    entries.forEach((e) => {
      if (e.subject_id && !byId.has(e.subject_id)) {
        byId.set(e.subject_id, {
          id: e.subject_id,
          name: e.subject_name,
          teacher: e.teacher_first_name || e.teacher_last_name
            ? `${e.teacher_first_name || ''} ${e.teacher_last_name || ''}`.trim()
            : null,
        });
      }
    });
    return Array.from(byId.values());
  }, [classInfo?.subjects, timetableData.entries]);

  const teachersInClass = useMemo(() => {
    const entries = timetableData.entries || [];
    const byId = new Map();
    entries.forEach((e) => {
      if (e.teacher_id && !byId.has(e.teacher_id)) {
        byId.set(e.teacher_id, {
          id: e.teacher_id,
          name: `${e.teacher_first_name || ''} ${e.teacher_last_name || ''}`.trim(),
        });
      }
    });
    return Array.from(byId.values());
  }, [timetableData.entries]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!classInfo) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/classes')} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Classes
        </Button>
        <p className="text-slate-400">Class not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="class-detail-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/classes')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-blue-400" />
              {classInfo.name}
            </h1>
            {classInfo.description && (
              <p className="text-slate-400 mt-1">{classInfo.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Sections */}
      {(classInfo.sections || []).length > 0 && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-slate-400" />
              Sections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {classInfo.sections.map((sec) => (
                <span key={sec.id} className="px-4 py-2 bg-slate-700 text-slate-200 rounded-lg text-sm">
                  Section {sec.name}
                  {sec.capacity != null && <span className="text-slate-500 ml-1">(capacity: {sec.capacity})</span>}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subjects in this class (from timetable) */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-400" />
              Subjects
            </CardTitle>
            <p className="text-slate-400 text-sm font-normal">Subjects assigned to this class</p>
          </CardHeader>
          <CardContent>
            {subjectsInClass.length === 0 ? (
              <p className="text-slate-400 py-4">No subjects assigned yet. Add entries in Timetable.</p>
            ) : (
              <ul className="space-y-2">
                {subjectsInClass.map((s) => (
                  <li key={s.id} className="flex justify-between items-center py-2 border-b border-slate-700 last:border-0">
                    <span className="text-white font-medium">{s.name}</span>
                    {s.teacher && <span className="text-slate-400 text-sm">— {s.teacher}</span>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Teachers for this class */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-green-400" />
              Teachers ({teachersInClass.length})
            </CardTitle>
            <p className="text-slate-400 text-sm font-normal">Teachers who teach this class</p>
          </CardHeader>
          <CardContent>
            {teachersInClass.length === 0 ? (
              <p className="text-slate-400 py-4">No teachers assigned. Add entries in Timetable.</p>
            ) : (
              <ul className="space-y-2">
                {teachersInClass.map((t) => (
                  <li key={t.id}>
                    <Link to={`/teachers/${t.id}`} className="text-blue-400 hover:text-blue-300 font-medium">
                      {t.name || 'Teacher'}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Fee structure for this class */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-amber-500" />
            Fee Structure
          </CardTitle>
          <p className="text-slate-400 text-sm font-normal">Fees defined for this class</p>
        </CardHeader>
        <CardContent>
          {feeStructure.length === 0 ? (
            <p className="text-slate-400 py-4">No fee structure set. Add from Fee Management → Fee Structure by Class.</p>
          ) : (
            <div className="rounded-lg border border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-700/50 text-left">
                    <th className="px-4 py-3 text-slate-300 font-medium">Fee Type</th>
                    <th className="px-4 py-3 text-slate-300 font-medium">Frequency</th>
                    <th className="px-4 py-3 text-slate-300 font-medium text-right">Amount (₹)</th>
                    <th className="px-4 py-3 text-slate-300 font-medium">Due Date</th>
                  </tr>
                </thead>
                <tbody>
                  {feeStructure.map((row) => (
                    <tr key={row.id} className="border-t border-slate-700">
                      <td className="px-4 py-3 text-white">{row.fee_type_name}</td>
                      <td className="px-4 py-3 text-slate-300">{row.fee_type_frequency || '—'}</td>
                      <td className="px-4 py-3 text-right font-medium text-white">₹{Number(row.amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-slate-300">{row.due_date ? new Date(row.due_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Students in this class */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-purple-400" />
            Students ({students.length})
          </CardTitle>
          <p className="text-slate-400 text-sm font-normal">Students assigned to this class</p>
        </CardHeader>
        <CardContent>
          {students.length === 0 ? (
            <p className="text-slate-400 py-4">No students in this class yet.</p>
          ) : (
            <div className="rounded-lg border border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-700/50 text-left">
                    <th className="px-4 py-3 text-slate-300 font-medium">Name</th>
                    <th className="px-4 py-3 text-slate-300 font-medium">Admission No</th>
                    <th className="px-4 py-3 text-slate-300 font-medium">Section</th>
                    <th className="px-4 py-3 text-slate-300 font-medium">Roll No</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                      <td className="px-4 py-3">
                        <Link to={`/students/${s.id}`} className="text-blue-400 hover:text-blue-300 font-medium">
                          {s.first_name} {s.last_name}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-slate-300">{s.admission_no || '—'}</td>
                      <td className="px-4 py-3 text-slate-300">{s.section_name ? `Section ${s.section_name}` : '—'}</td>
                      <td className="px-4 py-3 text-slate-300">{s.roll_no ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClassDetail;
